import React, { FC, Suspense } from 'react'
import { Loading, TitleController } from '@pandiafi/design-system'
import { useTranslation } from 'react-i18next'

import { DefectNoticeForm } from './Form'
import { DefectNoticeSuccess } from './Success'
import { DataLoader } from './DefectNoticeDataLoader'
import { ErrorHandler } from './DefectNoticeErrorHandler'

import { View, SidebarInfoSection, SidebarInfoSectionText } from 'components'
import { useContractPaths } from 'hooks/useContractPaths/useContractPaths'
import { ContactInfoErrorHandler } from 'views/ContactInfo/ContactInfoErrorHandler'
import { ContactInfoDataLoader } from 'views/ContactInfo/ContactInfoDataLoader'
import { MaintenanceContacts } from 'views/ContactInfo/MaintenanceContacts'
import DefectNotices from 'components/DefectNotices'

export type DefectNoticeViewState = 'form' | 'preview' | 'success'

export const DefectNoticeView: FC = () => {
  const [view, setView] = React.useState<DefectNoticeViewState>('form')
  const { t } = useTranslation(['common', 'defectNotice', 'titles'])
  const { frontPagePath } = useContractPaths()

  return (
    <TitleController title={t('titles:defectNotice')}>
      <View>
        <View.Header>
          <View.HeaderLink to={frontPagePath}>
            {t('common:views.home')}
          </View.HeaderLink>
          <View.HeaderTitle>
            {t('defectNotice:headings.defectNotice')}
          </View.HeaderTitle>
        </View.Header>
        <View.Sidebar>
          <DefectNotices />
          <SidebarInfoSection>
            <SidebarInfoSectionText>
              {t('defectNotice:texts.instructions')}
            </SidebarInfoSectionText>
          </SidebarInfoSection>
          <Suspense fallback={<Loading variant="details" />}>
            <ContactInfoErrorHandler>
              <ContactInfoDataLoader>
                {({ contacts }) => <MaintenanceContacts contacts={contacts} />}
              </ContactInfoDataLoader>
            </ContactInfoErrorHandler>
          </Suspense>
        </View.Sidebar>
        <View.Content>
          <Suspense fallback={<Loading variant="details" />}>
            <ErrorHandler>
              <DataLoader>
                {(data) =>
                  view === 'success' ? (
                    <DefectNoticeSuccess editMode={false} />
                  ) : (
                    <DefectNoticeForm {...data} view={view} setView={setView} />
                  )
                }
              </DataLoader>
            </ErrorHandler>
          </Suspense>
        </View.Content>
      </View>
    </TitleController>
  )
}
